import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output, inject } from '@angular/core';

import { combineLatest, Observable, distinctUntilChanged, map, switchMap } from 'rxjs';

import { LanguageService } from '@fcom/ui-translate';
import { MediaQueryService, PageMetaService } from '@fcom/common';
import { TripType } from '@fcom/core/interfaces';
import { CmsTaxonomy, CmsTaxonomyGeneral } from '@fcom/core-api';
import { GlobalBookingTravelClass, LocationRouteCffService } from '@fcom/core';
import { finShare } from '@fcom/rx';
import { FetchPriorityEnum } from '@fcom/ui-components';

import { BlockOffer, BlockOfferData } from '../../interfaces';
import { getHeaderLevel } from '../../services/cms-utils/cms-utils';

@Component({
  selector: 'fin-offer-grid',
  styleUrls: ['./offer-grid.component.scss'],
  templateUrl: './offer-grid.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class OfferGridComponent implements OnInit {
  private languageService = inject(LanguageService);
  private locationRouteCffService = inject(LocationRouteCffService);
  private pageMetaService = inject(PageMetaService);
  private mediaQueryService = inject(MediaQueryService);

  @Output()
  destinationClicked: EventEmitter<[BlockOffer, number]> = new EventEmitter();
  @Output()
  scrolledPastDestination: EventEmitter<{ offer: BlockOfferData; index: number }> = new EventEmitter();
  @Output()
  calendarClicked: EventEmitter<[Event, BlockOffer, GlobalBookingTravelClass, TripType, number]> = new EventEmitter();
  @Output()
  flightHotelClicked: EventEmitter<{
    amLink: string;
    destinationTitle: string;
    destinationCode: string;
    offer: BlockOfferData;
    index: number;
  }> = new EventEmitter();

  @Input()
  offers$: Observable<BlockOfferData[]>;
  @Input()
  tripType: TripType;
  @Input()
  title: string;
  @Input()
  cmsTags: CmsTaxonomy[];
  @Input()
  mobileCarousel = false;
  @Input()
  noHeaderPadding = false;

  @Input()
  fetchImagePriority?: FetchPriorityEnum;

  public readonly TAXONOMY: typeof CmsTaxonomyGeneral = CmsTaxonomyGeneral;

  selectedOriginLocationName$: Observable<string>;
  headerLevel: number | undefined;
  isMobile$: Observable<boolean>;

  readonly cmsStylesOverride = {
    'margin-top': '0.3rem',
    'margin-bottom': '0.9rem',
  };

  ngOnInit(): void {
    this.isMobile$ = this.mediaQueryService.isBreakpoint$('mobile');
    this.headerLevel = getHeaderLevel(this.cmsTags);

    /**
     * Fetch translated location name for selected origin
     */
    const origin$ = this.pageMetaService.defaultOriginLocationCode$.pipe(
      switchMap((origin) => this.locationRouteCffService.bestGuessFor(origin, this.languageService.localeValue)),
      distinctUntilChanged(),
      finShare()
    );

    this.selectedOriginLocationName$ = combineLatest([this.pageMetaService.defaultOriginLocationCode$, origin$]).pipe(
      map(([originLocationCode, originLocation]) => originLocation.cityName ?? originLocationCode)
    );
  }

  destinationClick([destinationItem, _travelClass]: [BlockOffer, GlobalBookingTravelClass], index: number): void {
    this.destinationClicked.emit([destinationItem, index]);
  }

  emitFlightHotelClick(
    event: { amLink: string; destinationTitle: string; destinationCode: string },
    offer: BlockOfferData,
    index: number
  ): void {
    this.flightHotelClicked.emit({
      ...event,
      offer,
      index,
    });
  }

  emitCalendarClick(
    [event, destinationItem, travelClass, tripType]: [Event, BlockOffer, GlobalBookingTravelClass, TripType],
    index: number
  ): void {
    this.calendarClicked.emit([event, destinationItem, travelClass, tripType, index]);
  }
}
